var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { Location } from '@angular/common';
import { OnDestroy, OnInit, } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Auth } from '@freelancer/auth';
import { ContestEntryFileType, CONTEST_ENTRY_AWARD_STATUSES, CONTEST_ENTRY_TEXT_FORMATS, CONTEST_ENTRY_VIDEO_FORMATS, Datastore, EntryAction, flatten, OrderByDirection, } from '@freelancer/datastore';
import { ModalRef } from '@freelancer/ui';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { CalloutPlacement, CalloutSize } from '@freelancer/ui/callout';
import { FileDisplaySize } from '@freelancer/ui/file-display';
import { HeadingType } from '@freelancer/ui/heading';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { ListItemComponent, ListItemPadding } from '@freelancer/ui/list-item';
import { Margin } from '@freelancer/ui/margin';
import { PictureDisplay } from '@freelancer/ui/picture';
import { RibbonColor, RibbonOrientation, RibbonPlacement, } from '@freelancer/ui/ribbon';
import { SpinnerSize } from '@freelancer/ui/spinner';
import { StickyBehaviour, StickyPosition } from '@freelancer/ui/sticky';
import { FontColor, FontStyle, FontType, FontWeight, TextAlign, TextSize, TextTransform, } from '@freelancer/ui/text';
import { ToastAlertColor, ToastAlertService, ToastAlertType, } from '@freelancer/ui/toast-alert';
import { TooltipPosition } from '@freelancer/ui/tooltip';
import { AvatarSize } from '@freelancer/ui/user-avatar';
import { toNumber } from '@freelancer/utils';
import { ContestCommentContextTypeApi, EntryStatusApi, } from 'api-typings/contests/contests';
import { PerfectScrollbarComponent } from 'ngx-perfect-scrollbar';
import * as Rx from 'rxjs';
import { distinctUntilKeyChanged, flatMap, map, tap } from 'rxjs/operators';
import { PreviewModalResponse } from './preview-modal.types';
export var PreviewModalTab;
(function (PreviewModalTab) {
    PreviewModalTab[PreviewModalTab["COMMENTS"] = 0] = "COMMENTS";
    PreviewModalTab[PreviewModalTab["ANNOTATIONS"] = 1] = "ANNOTATIONS";
})(PreviewModalTab || (PreviewModalTab = {}));
var PreviewModalComponent = /** @class */ (function () {
    /**
     * Placeholder variables.
     * TODO: Replace variables with dynamic data -- hooked from the backend.
     */
    function PreviewModalComponent(auth, datastore, location, modalRef, toastAlertService) {
        this.auth = auth;
        this.datastore = datastore;
        this.location = location;
        this.modalRef = modalRef;
        this.toastAlertService = toastAlertService;
        this.AvatarSize = AvatarSize;
        this.ButtonColor = ButtonColor;
        this.ButtonSize = ButtonSize;
        this.CalloutPlacement = CalloutPlacement;
        this.CalloutSize = CalloutSize;
        this.ContestEntryFileType = ContestEntryFileType;
        this.FileDisplaySize = FileDisplaySize;
        this.FontColor = FontColor;
        this.TextSize = TextSize;
        this.FontStyle = FontStyle;
        this.FontType = FontType;
        this.FontWeight = FontWeight;
        this.HeadingType = HeadingType;
        this.IconColor = IconColor;
        this.IconSize = IconSize;
        this.ListItemPadding = ListItemPadding;
        this.Margin = Margin;
        this.PictureDisplay = PictureDisplay;
        this.PreviewModalTab = PreviewModalTab;
        this.RibbonColor = RibbonColor;
        this.RibbonOrientation = RibbonOrientation;
        this.RibbonPlacement = RibbonPlacement;
        this.SpinnerSize = SpinnerSize;
        this.StickyBehaviour = StickyBehaviour;
        this.StickyPosition = StickyPosition;
        this.TextAlign = TextAlign;
        this.TextTransform = TextTransform;
        this.ToastAlertColor = ToastAlertColor;
        this.ToastAlertType = ToastAlertType;
        this.TooltipPosition = TooltipPosition;
        this.THUMBNAIL_LIMIT_DESKTOP = 5;
        this.THUMBNAIL_LIMIT_MOBILE = 7;
        this.REJECT_ENTRY_CONTENTS = { status: EntryStatusApi.ELIMINATED };
        this.RECONSIDER_ENTRY_CONTENTS = { status: EntryStatusApi.ACTIVE };
        this.ratingControl = new FormControl();
        this.showPdfPreviewImageFallback = false;
        this.isPdfPreviewImageLoaded = false;
        this.currentTab = PreviewModalTab.COMMENTS;
        this.commentDeletePromisesMap = new Map();
        this.commentUndeletePromisesMap = new Map();
        this.commentsRepliesMap = new Map();
        this.REPLIES_DEFAULT_LIMIT = 2;
        this.REPLIES_LIMIT_INCREMENT = 5;
        this.PARENT_COMMENT_LIMIT_INCREMENT = 10;
        this.parentCommentLimitSubject$ = new Rx.BehaviorSubject(this.PARENT_COMMENT_LIMIT_INCREMENT);
        this.parentCommentLimit$ = this.parentCommentLimitSubject$.asObservable();
        this.deletedCommentsSubject$ = new Rx.BehaviorSubject([]);
        this.deletedComments$ = this.deletedCommentsSubject$.asObservable();
    }
    PreviewModalComponent.prototype.ngOnInit = function () {
        var _this = this;
        this.entryIdSubject$ = new Rx.BehaviorSubject(this.initialEntryId);
        this.entryId$ = this.entryIdSubject$.asObservable();
        var initialEntryIndex = this.entryIds.findIndex(function (id) { return id === _this.initialEntryId; });
        this.currentEntryIndexSubject$ = new Rx.BehaviorSubject(initialEntryIndex);
        this.currentEntryIndex$ = this.currentEntryIndexSubject$.asObservable();
        // Datastore call needed to have the entry status change reflected on the modal upon user CTA
        var entriesDocument = this.datastore.document('contestQuickviewEntries', this.entryId$);
        this.entry$ = entriesDocument.valueChanges();
        this.entryFetchStatus$ = entriesDocument.status$;
        this.entrySubscription = this.entry$.subscribe(function (entry) {
            _this.location.replaceState(entry.seoUrl);
        });
        var freelancerDocument = this.datastore.document('users', this.entry$.pipe(map(function (entry) { return entry.ownerId; })));
        this.freelancer$ = freelancerDocument.valueChanges();
        this.freelancerFetchStatus$ = freelancerDocument.status$;
        this.isHighlighted$ = this.entry$.pipe(map(function (entry) {
            return !!(entry.upgrades &&
                entry.upgrades.highlight &&
                !_this.hasContestAwardedEntry &&
                ![
                    EntryStatusApi.ELIMINATED,
                    EntryStatusApi.WITHDRAWN,
                    EntryStatusApi.WITHDRAWN_ELIMINATED,
                ].includes(entry.status));
        }));
        this.isActive$ = this.entry$.pipe(map(function (entry) {
            return !_this.hasContestAwardedEntry &&
                entry.status === EntryStatusApi.ACTIVE;
        }));
        this.isRejected$ = this.entry$.pipe(map(function (entry) {
            return [
                EntryStatusApi.ELIMINATED,
                EntryStatusApi.WITHDRAWN_ELIMINATED,
            ].includes(entry.status);
        }));
        this.isWithdrawn$ = this.entry$.pipe(map(function (entry) {
            return [
                EntryStatusApi.WITHDRAWN,
                EntryStatusApi.WITHDRAWN_ELIMINATED,
            ].includes(entry.status);
        }));
        this.isSealed$ = this.entry$.pipe(map(function (entry) { return !!(entry.upgrades && entry.upgrades.sealed); }));
        this.isAwarded$ = this.entry$.pipe(map(function (entry) { return CONTEST_ENTRY_AWARD_STATUSES.includes(entry.status); }));
        this.isBought$ = this.entry$.pipe(map(function (entry) { return entry.status === EntryStatusApi.BOUGHT; }));
        this.isAwardedOrBought$ = Rx.combineLatest([
            this.isAwarded$,
            this.isBought$,
        ]).pipe(map(function (_a) {
            var isAwarded = _a[0], isBought = _a[1];
            return isAwarded || isBought;
        }));
        this.isBeingSold$ = this.entry$.pipe(map(function (entry) {
            return !!(_this.hasContestAwardedEntry &&
                entry.status === EntryStatusApi.ACTIVE &&
                (entry.sellPrice || _this.contest.prize));
        }));
        var entryEmissionOnlyWhenIdChanges$ = this.entry$.pipe(distinctUntilKeyChanged('id'), tap(function (entry) {
            // unsubscribe if there's an existing subscription
            // to prevent calling rate entry function on entry change
            // when setting initial rating
            if (_this.ratingSubscription) {
                _this.ratingSubscription.unsubscribe();
            }
            _this.ratingControl.setValue(entry.rating);
            _this.ratingSubscription = _this.ratingControl.valueChanges.subscribe(function (rating) { return _this.onRateEntry({ rating: rating }); });
            _this.showPdfPreviewImageFallback = false;
            _this.isPdfPreviewImageLoaded = false;
        }));
        this.thumbnails$ = entryEmissionOnlyWhenIdChanges$.pipe(map(function (entry) {
            return entry.files.map(function (file) { return ({
                src: file.thumbnail80Url || '',
                alt: ' ',
            }); });
        }));
        var userId$ = this.auth.getUserId().pipe(map(function (id) { return toNumber(id); }));
        this.user$ = this.datastore.document('users', userId$).valueChanges();
        this.isEntryOwner$ = Rx.combineLatest([userId$, this.entry$]).pipe(map(function (_a) {
            var userId = _a[0], entry = _a[1];
            return userId === entry.ownerId;
        }));
        this.currentFileIndexSubject$ = new Rx.BehaviorSubject(this.imageSliderIndex);
        this.currentFileIndex$ = this.currentFileIndexSubject$.asObservable();
        this.currentFileNumber$ = this.currentFileIndex$.pipe(map(function (currentFileIndex) { return currentFileIndex + 1; }));
        this.currentFile$ = Rx.combineLatest([
            this.entry$,
            this.currentFileIndex$,
        ]).pipe(map(function (_a) {
            var entry = _a[0], currentFileIndex = _a[1];
            return entry.files[currentFileIndex];
        }));
        this.parentCommentsCollection = this.datastore.collection('contestComments', function (query) {
            return query
                .where('contextId', '==', _this.entryId$)
                .where('contextType', '==', ContestCommentContextTypeApi.ENTRY)
                .where('parentId', '==', undefined)
                .orderBy('dateLastComment', OrderByDirection.DESC)
                .limit(_this.parentCommentLimit$);
        });
        // TODO: Replace TEST_FILE_ID to get the current file ID
        this.parentAnnotationsCollection = this.datastore.collection('contestComments', function (query) {
            return query
                .where('contextId', '==', _this.currentFile$.pipe(map(function (currentFile) { return currentFile.id; })))
                .where('contextType', '==', ContestCommentContextTypeApi.ANNOTATION)
                .where('parentId', '==', undefined)
                .orderBy('dateLastComment', OrderByDirection.DESC)
                .limit(_this.parentCommentLimit$);
        });
        // Get only annotations that are not deleted
        this.parentAnnotations$ = this.parentAnnotationsCollection
            .valueChanges()
            .pipe(map(function (annotations) {
            return annotations.filter(function (annotation) { return !annotation.isDeleted; });
        }));
        var replyQueriesSubject$ = new Rx.BehaviorSubject([]);
        var replyQueries$ = replyQueriesSubject$.asObservable();
        // Get replies per parent comment
        this.repliesSubscription = Rx.combineLatest([
            this.parentCommentsCollection.valueChanges(),
            this.parentAnnotations$,
        ]).subscribe(function (_a) {
            var parentComments = _a[0], parentAnnotations = _a[1];
            parentComments.concat(parentAnnotations).forEach(function (parentComment) {
                if (!_this.commentsRepliesMap.has(parentComment.id)) {
                    var repliesLimitSubject$_1 = new Rx.BehaviorSubject(_this.REPLIES_DEFAULT_LIMIT);
                    // We want to display the latest replies based on a limit in ascending order.
                    // To get the latest replies without having to provide an offset, the backend
                    // queries for replies in descending order with the limit. We then reverse the
                    // list to be displayed in ascending order.
                    var queryResult$ = _this.datastore.collection('contestComments', function (query) {
                        return query
                            .where('parentId', '==', parentComment.id)
                            .orderBy('dateLastComment', OrderByDirection.DESC)
                            .limit(repliesLimitSubject$_1.asObservable());
                    });
                    // When deleting a comment, it isn't removed from the collection, but instead
                    // it's isDeleted field is set to true. This is so that we can support
                    // undeleting the comment. On the template, we have to filter comments
                    // based on isDeleted.
                    var replies$ = queryResult$.valueChanges().pipe(map(function (comments) {
                        return comments
                            .filter(function (comment) { return !comment.isDeleted; })
                            .slice()
                            .reverse();
                    }));
                    // We use unfilteredReplies$ in the checks to properly display the 'View older replies' CTAs.
                    // This makes sure that the comment count is consistent regardless of whether there are deleted
                    // comments or not.
                    var unfilteredReplies$ = queryResult$.valueChanges();
                    var fetchStatus$ = queryResult$.status$;
                    _this.commentsRepliesMap.set(parentComment.id, {
                        replies$: replies$,
                        unfilteredReplies$: unfilteredReplies$,
                        fetchStatus$: fetchStatus$,
                        limitSubject$: repliesLimitSubject$_1,
                        limit$: repliesLimitSubject$_1.asObservable(),
                    });
                    replyQueriesSubject$.next(replyQueriesSubject$.value.concat([replies$]));
                }
            });
        });
        // Get replies user IDs
        this.repliesUserIds$ = replyQueries$.pipe(flatMap(function (replyQueries) {
            return Rx.combineLatest(replyQueries).pipe(map(flatten), map(function (allReplies) { return allReplies.map(function (reply) { return reply.fromUserId; }); }));
        }));
        // Get parent comments user IDs
        var parentCommentsUserIds$ = this.parentCommentsCollection
            .valueChanges()
            .pipe(map(function (parentComments) {
            return parentComments.map(function (parentComment) { return parentComment.fromUserId; });
        }));
        // Get parent annotations user IDs
        var parentAnnotationsUserIds$ = this.parentAnnotations$.pipe(map(function (parentAnnotations) {
            return parentAnnotations.map(function (parentAnnotation) { return parentAnnotation.fromUserId; });
        }));
        // Get all comments users
        this.commentsUsers$ = this.datastore
            .collection('users', function (query) {
            return Rx.combineLatest([
                _this.repliesUserIds$,
                parentCommentsUserIds$,
                parentAnnotationsUserIds$,
            ]).pipe(map(function (_a) {
                var repliesUserIds = _a[0], parentCommentsUserIds = _a[1], parentAnnotationsUserIds = _a[2];
                return query.where('id', 'in', repliesUserIds.concat(parentCommentsUserIds, parentAnnotationsUserIds));
            }));
        })
            .valueChanges()
            .pipe(map(function (users) { return new Map(users.map(function (user) { return [user.id, user]; })); }));
        this.currentFileType$ = this.currentFile$.pipe(map(function (file) {
            var fileType = file.name.split('.').pop();
            fileType = fileType ? fileType.toLowerCase() : file.name;
            if (CONTEST_ENTRY_VIDEO_FORMATS.includes(fileType)) {
                return ContestEntryFileType.VIDEO;
            }
            if (CONTEST_ENTRY_TEXT_FORMATS.includes(fileType)) {
                return ContestEntryFileType.TEXT;
            }
            return ContestEntryFileType.IMAGE;
        }));
    };
    PreviewModalComponent.prototype.handlePdfPreviewImageError = function () {
        this.showPdfPreviewImageFallback = true;
    };
    PreviewModalComponent.prototype.handlePdfPreviewImageLoaded = function () {
        this.isPdfPreviewImageLoaded = true;
    };
    PreviewModalComponent.prototype.onAwardEntry = function (entry, freelancer) {
        // TODO: Open award modal directly in preview modal after T82677 is done
        this.modalRef.close({
            response: PreviewModalResponse.AWARD,
            entry: entry,
            freelancer: freelancer,
            canMultiAward: this.canMultiAward,
        });
    };
    PreviewModalComponent.prototype.onBuyEntry = function (entry, freelancer) {
        this.modalRef.close({
            response: PreviewModalResponse.BUY,
            contest: this.contest,
            entry: entry,
            freelancer: freelancer,
        });
    };
    PreviewModalComponent.prototype.onSlideChange = function (index) {
        this.currentFileIndexSubject$.next(index);
        this.currentTab = PreviewModalTab.COMMENTS;
    };
    PreviewModalComponent.prototype.onViewMoreComments = function () {
        this.parentCommentLimitSubject$.next(this.parentCommentLimitSubject$.getValue() +
            this.PARENT_COMMENT_LIMIT_INCREMENT);
    };
    PreviewModalComponent.prototype.onViewMoreReplies = function (replyParentId) {
        var replyDetails = this.commentsRepliesMap.get(replyParentId);
        if (replyDetails) {
            replyDetails.limitSubject$.next(replyDetails.limitSubject$.getValue() + this.REPLIES_LIMIT_INCREMENT);
            this.commentsRepliesMap.set(replyParentId, replyDetails);
        }
    };
    PreviewModalComponent.prototype.onDeleteComment = function (comment) {
        var _this = this;
        // Add toast container
        this.deletedCommentsSubject$.next(this.deletedCommentsSubject$.getValue().concat([
            comment,
        ]));
        // We can use parentCommentsCollection to delete both parent comments and replies
        var commentDeletePromise = this.parentCommentsCollection.remove(comment.id);
        this.commentDeletePromisesMap.set(comment.id, commentDeletePromise);
        commentDeletePromise.then(function (response) {
            if (response.status === 'success') {
                // Open toast on success
                _this.toastAlertService.open("" + comment.id);
            }
        });
    };
    PreviewModalComponent.prototype.onUndeleteComment = function (comment) {
        var _this = this;
        // We can use parentCommentsCollection to undelete both parent comments and replies
        var commentUndeletePromise = this.parentCommentsCollection.update(comment.id, __assign({}, comment, { isDeleted: false }));
        this.commentUndeletePromisesMap.set(comment.id, commentUndeletePromise);
        commentUndeletePromise.then(function (response) {
            if (response.status === 'success') {
                // Remove toast container on success
                _this.deletedCommentsSubject$.next(_this.deletedCommentsSubject$
                    .getValue()
                    .filter(function (deletedComment) { return deletedComment.id !== comment.id; }));
            }
        });
    };
    PreviewModalComponent.prototype.showErrorsOnHeader = function () {
        var headerEntryActions = [
            EntryAction.RATE,
            EntryAction.REJECT,
            EntryAction.RECONSIDER,
        ];
        return headerEntryActions.includes(this.entryAction);
    };
    PreviewModalComponent.prototype.onRateEntry = function (changes) {
        this.entryAction = EntryAction.RATE;
        this.updateResponse$ = this.updateEntry(changes);
    };
    PreviewModalComponent.prototype.onRejectEntry = function (changes) {
        this.entryAction = EntryAction.REJECT;
        this.rejectResponse$ = this.updateEntry(changes);
        this.updateResponse$ = this.rejectResponse$;
    };
    PreviewModalComponent.prototype.onReconsiderEntry = function (changes) {
        this.entryAction = EntryAction.RECONSIDER;
        this.reconsiderResponse$ = this.updateEntry(changes);
        this.updateResponse$ = this.reconsiderResponse$;
    };
    PreviewModalComponent.prototype.onLikeEntry = function (entry, changes) {
        this.entryAction = entry.isLiked ? EntryAction.DISLIKE : EntryAction.LIKE;
        this.updateResponse$ = this.updateEntry(changes);
    };
    PreviewModalComponent.prototype.updateEntry = function (changes) {
        return this.datastore
            .document('contestQuickviewEntries', this.entryId$)
            .update(changes);
    };
    PreviewModalComponent.prototype.viewEntry = function (index) {
        this.currentFileIndexSubject$.next(0);
        this.currentTab = PreviewModalTab.COMMENTS;
        // Set index when hitting the end of the list. It should behave like a
        // circular list.
        var nextEntryIndex = index;
        if (index < 0) {
            nextEntryIndex = this.entryIds.length - 1;
        }
        else if (index === this.entryIds.length) {
            nextEntryIndex = 0;
        }
        // reset stock list expandable state
        if (this.stockListItem) {
            this.stockListItem.control.setValue(false);
        }
        // unset error message
        this.updateResponse$ = undefined;
        this.currentEntryIndexSubject$.next(nextEntryIndex);
        this.entryIdSubject$.next(this.entryIds[nextEntryIndex]);
    };
    PreviewModalComponent.prototype.handleChatClick = function (entry) {
        this.modalRef.close({ response: PreviewModalResponse.START_CHAT, entry: entry });
    };
    PreviewModalComponent.prototype.handleTabSelect = function (tab) {
        this.currentTab = tab;
    };
    PreviewModalComponent.prototype.handleClose = function () {
        this.modalRef.close();
    };
    PreviewModalComponent.prototype.ngOnDestroy = function () {
        if (this.ratingSubscription) {
            this.ratingSubscription.unsubscribe();
        }
        if (this.entrySubscription) {
            this.entrySubscription.unsubscribe();
        }
        if (this.repliesSubscription) {
            this.repliesSubscription.unsubscribe();
        }
    };
    return PreviewModalComponent;
}());
export { PreviewModalComponent };
